import { motion } from "framer-motion";
import styled from "styled-components";
import { Card, fadeIn, formatDate, PageContainer, SearchInput, Table, TableCell, TableHeader, TableRow } from "../styles/PageStyles";
import PageHeaderWithInfo from "../components/common/PageHeaderWithInfo";
import { faWindowRestore } from "@fortawesome/free-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import { FaBuilding } from "react-icons/fa";
import { faBuilding } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef, useState } from "react";
import { getCompaniesByFilter } from "../services/companyService";

const Container = styled.div`
  padding: 1rem;
  max-width: 1600px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: start;
  margin-bottom: 3rem;
  display: flex;
  align-item: center;
  justify-content: space-between
`;

const Title = styled.h1`
  font-size: 1.9rem;
  font-weight: 700;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  // font-family: 'Manrope', sans-serif;
  margin-bottom: 0.5rem;
`;

const Button = styled(motion.button)`
  padding: 0.9rem 2rem;
  background: ${props => {
    if (props.primary) return 'var(--gradient-primary)';
    if (props.secondary) return 'transparent';
    return '#ffffff';
  }};
  border: 1px solid ${props => {
    if (props.primary) return 'transparent';
    if (props.secondary) return 'var(--primary)';
    return 'rgba(0, 102, 255, 0.2)';
  }};
  border-radius: 8px;
  color: ${props => {
    if (props.primary) return 'white';
    if (props.secondary) return 'var(--primary)';
    return 'var(--text-primary)';
  }};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => {
    if (props.primary) return 'var(--gradient-primary)';
    if (props.secondary) return 'rgba(0, 102, 255, 0.05)';
    return 'rgba(0, 102, 255, 0.03)';
  }};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ContentLayout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  height: fit-content;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const NavItem = styled.button`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: ${props => props.active ? '#EBF5FF' : 'transparent'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;
  text-align: left;
  
  &:hover {
    background: ${props => props.active ? '#EBF5FF' : '#f9fafb'};
  }
`;

const NavIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.active ? '#0066FF' : '#E0EAFF'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${props => props.active ? '#ffffff' : '#0066FF'};
  font-size: 1.2rem;
`;

const NavContent = styled.div`
  flex: 1;
`;

const NavTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.active ? '#0066FF' : '#1f2937'};
  margin-bottom: 0.25rem;
`;

const NavDescription = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.4;
`;

const MainContent = styled.div`
  min-width: 0;
`;

const TabContent = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
`;

const Company = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('companies');
    const [searchTerm , setSearchTerm] = useState('');
    const [companies, setCompanies] = useState([]);
    const [apps, setApps] = useState([]);
    const timerRef = useRef();

    const handleEditCompany = (companyId) => {
      navigate(`/manage/company/${companyId}`);
    }

    const navigeToCompany = () => {
        navigate('/create/company')
    }

    useEffect(() => {
      try {
        const fetchCompanies = async () => {
          const responce = await getCompaniesByFilter();
          setCompanies(responce || []);
        }
        fetchCompanies();
      } catch (error) {
        console.log("Error while fetching the companies.")
      }
    },[])

  const filteredCompanies = (companies || []).filter(company =>
    company?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company?.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (value) => {

    if(timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, 300);
  }

    return (
           <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <PageHeaderWithInfo
          icon={faBuilding}
          title="Company Management"
          description="Manage companies and explore the Agent Apps Market Place."
        />
        
        <ContentLayout>
          <Sidebar>
            <NavItem 
              active={activeTab === 'companies'} 
              onClick={() => setActiveTab('companies')}
            >
              <NavIcon active={activeTab === 'companies'}>
                🏢
              </NavIcon>
              <NavContent>
                <NavTitle active={activeTab === 'companies'}>Companies</NavTitle>
                <NavDescription>Company name and details</NavDescription>
              </NavContent>
            </NavItem>
            
            <NavItem 
              active={activeTab === 'marketplace'} 
              onClick={() => setActiveTab('marketplace')}
            >
              <NavIcon active={activeTab === 'marketplace'}>
                🛍️
              </NavIcon>
              <NavContent>
                <NavTitle active={activeTab === 'marketplace'}>Agent Apps</NavTitle>
                <NavDescription>Browse & install agent apps</NavDescription>
              </NavContent>
            </NavItem>
          </Sidebar>

          <MainContent>
            <TabContent active={activeTab === 'companies'}>
              <div style={{ display: 'flex', gap: "1rem", marginBottom: '1.5rem' }}>
                <SearchInput
                  type="text"
                  placeholder="Search Company..."
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
                <Button primary onClick={() => navigeToCompany()}>Add Company</Button>
              </div>
              <Card>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Name</TableHeader>
                      <TableHeader>Description</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Last Updated</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompanies.length > 0 && filteredCompanies.map((company) => (
                      <TableRow key={company.id}>
                        <TableCell>{company.name}</TableCell>
                        <TableCell>{company.description}</TableCell>
                        <TableCell>{company.status || '-'}</TableCell>
                        <TableCell>{formatDate(company.updated_at)}</TableCell>
                        <TableCell>
                          <div style={{display:'flex', gap:'10px', height:'100%', alignItems:'center'}}>
                            <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} onClick={() => handleEditCompany(company.id)}>Edit</Button>
                            <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Remove</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </Card>
            </TabContent>

            <TabContent active={activeTab === 'marketplace'}>
              <div style={{ display: 'flex', gap: "1rem", marginBottom: '1.5rem' }}>
                <SearchInput
                  type="text"
                  placeholder="Search Apps..."
                />
                <Button primary>Add New App</Button>
              </div>
              <Card>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>App Name</TableHeader>
                      <TableHeader>Description</TableHeader>
                      <TableHeader>Category</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.length > 0 ? (
                      apps.map((app) => (
                        <TableRow key={app.id}>
                          <TableCell>{app.name}</TableCell>
                          <TableCell>{app.description}</TableCell>
                          <TableCell>{app.category}</TableCell>
                          <TableCell>{app.status}</TableCell>
                          <TableCell>
                            <div style={{display:'flex', gap:'10px', height:'100%', alignItems:'center'}}>
                              <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>View</Button>
                              <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Install</Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                          No apps available in the marketplace
                        </TableCell>
                      </TableRow>
                    )}
                  </tbody>
                </Table>
              </Card>
            </TabContent>
          </MainContent>
        </ContentLayout>
      </motion.div>
    </PageContainer>
    )
}


export default Company;